import React, { useState } from 'react';
import { Coffee, Heart, X, IndianRupee, Sparkles, QrCode, Globe, Copy, Check } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

const SupportModal = ({ isOpen, onClose }) => {
    const [amount, setAmount] = useState('100');
    const [isCustom, setIsCustom] = useState(false);
    const [method, setMethod] = useState('upi'); // 'upi' or 'intl'
    const [copied, setCopied] = useState(false);

    const amounts = ['50', '100', '500'];
    const upiId = "dhavalumraliya777@okicici";

    // Generate UPI QR Code URL
    // Format: upi://pay?pa=dhavalumraliya777@okicici&pn=AnimationForYou&am=100&cu=INR
    const upiDeepLink = `upi://pay?pa=${upiId}&pn=AnimationForYou&am=${amount}&cu=INR`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upiDeepLink)}`;

    const handleCopyUpi = () => {
        navigator.clipboard.writeText(upiId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleIntlSupport = () => {
        window.open(`https://buymeacoffee.com/manushree/`, '_blank', 'noopener,noreferrer');
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[420px] bg-[#0F172A] border-white/10 p-0 overflow-hidden outline-none shadow-2xl">
                {/* Dynamic Header */}
                <div className={cn(
                    "h-24 relative flex items-center justify-center overflow-hidden transition-colors duration-500",
                    method === 'upi' ? "bg-gradient-to-br from-emerald-600 to-teal-700" : "bg-gradient-to-br from-violet-600 to-indigo-700"
                )}>
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20"></div>
                    <div className="relative z-10 w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center shadow-2xl animate-bounce">
                        {method === 'upi' ? <QrCode className="w-8 h-8 text-white" /> : <Coffee className="w-8 h-8 text-white" />}
                    </div>
                    <Sparkles className="absolute top-4 right-4 text-white/40 w-4 h-4 animate-pulse" />
                </div>

                <div className="p-8">
                    <DialogHeader className="mb-6">
                        <DialogTitle className="text-2xl font-black text-white text-center tracking-tighter">
                            Support I <Heart className="inline w-5 h-5 text-red-500 fill-current animate-pulse" /> ANIMATION
                        </DialogTitle>
                        <div className="flex bg-white/5 p-1 rounded-lg mt-4 max-w-[240px] mx-auto border border-white/5">
                            <button
                                onClick={() => setMethod('upi')}
                                className={cn(
                                    "flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-[10px] font-black uppercase tracking-wider transition-all",
                                    method === 'upi' ? "bg-white/10 text-white shadow-sm" : "text-slate-500 hover:text-slate-300"
                                )}
                            >
                                <IndianRupee className="w-3 h-3" /> UPI
                            </button>
                            <button
                                onClick={() => setMethod('intl')}
                                className={cn(
                                    "flex-1 flex items-center justify-center gap-2 py-1.5 rounded-md text-[10px] font-black uppercase tracking-wider transition-all",
                                    method === 'intl' ? "bg-white/10 text-white shadow-sm" : "text-slate-500 hover:text-slate-300"
                                )}
                            >
                                <Globe className="w-3 h-3" /> Intl
                            </button>
                        </div>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* Amount Selection */}
                        <div className="grid grid-cols-3 gap-3">
                            {amounts.map((amt) => (
                                <button
                                    key={amt}
                                    onClick={() => {
                                        setAmount(amt);
                                        setIsCustom(false);
                                    }}
                                    className={cn(
                                        "py-3 rounded-xl border-2 transition-all duration-300 font-bold text-sm flex flex-col items-center gap-1",
                                        !isCustom && amount === amt
                                            ? (method === 'upi' ? "bg-emerald-500 border-emerald-400 text-white" : "bg-violet-500 border-violet-400 text-white")
                                            : "bg-white/5 border-white/5 text-slate-400 hover:border-white/20 hover:text-white"
                                    )}
                                >
                                    <div className="flex items-center">
                                        <IndianRupee className="w-3 h-3" />
                                        <span>{amt}</span>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Custom Amount */}
                        <div
                            onClick={() => setIsCustom(true)}
                            className={cn(
                                "p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center justify-between",
                                isCustom
                                    ? (method === 'upi' ? "bg-emerald-500/10 border-emerald-500" : "bg-violet-500/10 border-violet-500")
                                    : "bg-white/5 border-white/5 hover:border-white/10"
                            )}
                        >
                            <span className={cn("text-[10px] font-black uppercase tracking-widest", isCustom ? (method === 'upi' ? "text-emerald-400" : "text-violet-400") : "text-slate-500")}>
                                Custom
                            </span>
                            {isCustom && (
                                <div className="flex items-center gap-2 max-w-[120px]">
                                    <IndianRupee className="w-3 h-3 text-slate-400" />
                                    <Input
                                        type="number"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        className="h-8 bg-transparent border-none text-right focus-visible:ring-0 p-0 text-white font-black text-lg"
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>

                        {method === 'upi' ? (
                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                <div className="bg-white p-3 rounded-2xl mx-auto w-[180px] h-[180px] shadow-2xl relative group">
                                    <img src={qrCodeUrl} alt="UPI QR Code" className="w-full h-full" />
                                    <div className="absolute inset-0 bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
                                        <p className="text-[10px] font-black text-slate-900 text-center px-4 uppercase tracking-tighter">
                                            Scan with GPay, PhonePe, or Paytm
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between bg-white/5 border border-white/5 rounded-xl px-4 py-3">
                                    <div className="flex flex-col">
                                        <span className="text-[8px] text-slate-500 font-black uppercase tracking-tighter">UPI ID</span>
                                        <span className="text-[10px] text-white font-bold">{upiId}</span>
                                    </div>
                                    <button
                                        onClick={handleCopyUpi}
                                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-slate-400 hover:text-white"
                                    >
                                        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <Button
                                onClick={handleIntlSupport}
                                className="w-full h-14 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white font-black text-base uppercase tracking-[0.1em] rounded-xl shadow-xl shadow-violet-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                            >
                                Support with BMC
                            </Button>
                        )}

                        <p className="text-[8px] text-slate-500 text-center font-black uppercase tracking-widest opacity-50">
                            {method === 'upi' ? "Direct transfer to creator" : "Secured by Buy Me a Coffee"}
                        </p>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SupportModal;
